// ════════════════════════════════════════════════════════════════════
// src/storeVisits/photoUpload.js
//
// Photo upload component for the SVR detailed/short form (Sprint 2.3 part 2).
// Replaces the renderPhotosPlaceholder() slots in form-detailed.js.
//
// Flow per photo:
//   1. Compress client-side (canvas resize → JPEG, keeps well under 5MB cap)
//   2. POST /api/store-visit-photos/sign-upload → { upload_url, storage_path }
//   3. PUT compressed blob to upload_url (Supabase Storage signed URL)
//   4. POST /api/store-visit-photos (record metadata) → { id, signed_url }
//   5. Show thumbnail
//
// Delete: DELETE /api/store-visit-photos/:id (removes DB row + Storage object)
//
// DEPENDENCIES:
//   - Sprint 2.2 photo endpoints live + smoke-test green (verifies the
//     Supabase Storage round-trip actually works)
//   - A visit_id must exist before photos can attach. form-detailed.js
//     creates a draft visit on open (Sprint 2.3 part 4) and passes the id
//     here. If visitId is null, the component shows a "save first" message.
//
// Server enforces a 5-photo-per-section cap (6th sign-upload → 400). This
// component also disables the picker at 5 for good UX.
//
// Re: `DB` — bare DB (db.js uses let/const, not on window).
// ════════════════════════════════════════════════════════════════════

import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

const MAX_PHOTOS_PER_SECTION = 5;
const COMPRESS_MAX_DIM = 1600;   // px, longest edge
const COMPRESS_QUALITY = 0.8;    // JPEG quality

function getToken() {
  return adminToken();   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
}
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}


// ──────────────────────────────────────────────────────────────────
// Client-side compression
// ──────────────────────────────────────────────────────────────────

function loadImage(file) {
  return new Promise(function (resolve, reject) {
    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = function () { URL.revokeObjectURL(objUrl); resolve(img); };
    img.onerror = function () { URL.revokeObjectURL(objUrl); reject(new Error('Could not read image')); };
    img.src = objUrl;
  });
}

async function compressImage(file) {
  // Non-images (shouldn't happen via capture, but guard) → return as-is
  if (!/^image\//.test(file.type)) return file;
  const img = await loadImage(file);
  let w = img.naturalWidth, h = img.naturalHeight;
  if (w > COMPRESS_MAX_DIM || h > COMPRESS_MAX_DIM) {
    const scale = COMPRESS_MAX_DIM / Math.max(w, h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  const blob = await new Promise(function (resolve) {
    canvas.toBlob(resolve, 'image/jpeg', COMPRESS_QUALITY);
  });
  return blob || file;  // fallback to original if toBlob unsupported
}


// ──────────────────────────────────────────────────────────────────
// API calls
// ──────────────────────────────────────────────────────────────────

async function signUpload(visitId, sectionKey) {
  const res = await fetch(getWorkerUrl() + '/api/store-visit-photos/sign-upload', { cache: 'no-store',
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ store_visit_id: visitId, section_key: sectionKey, mime_type: 'image/jpeg' }),
  });
  if (!res.ok) {
    const t = await res.text().catch(function () { return ''; });
    throw new Error('sign-upload ' + res.status + ': ' + t.slice(0, 140));
  }
  return res.json();  // { upload_url, token, storage_path }
}

async function putToStorage(uploadUrl, blob) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
  });
  if (!res.ok) {
    const t = await res.text().catch(function () { return ''; });
    throw new Error('storage PUT ' + res.status + ': ' + t.slice(0, 140));
  }
}

async function recordMetadata(visitId, sectionKey, storagePath) {
  const res = await fetch(getWorkerUrl() + '/api/store-visit-photos', { cache: 'no-store',
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ store_visit_id: visitId, section_key: sectionKey, storage_path: storagePath }),
  });
  if (!res.ok) {
    const t = await res.text().catch(function () { return ''; });
    throw new Error('record ' + res.status + ': ' + t.slice(0, 140));
  }
  return res.json();  // { id, signed_url, ... }
}

async function deletePhoto(photoId) {
  const res = await fetch(getWorkerUrl() + '/api/store-visit-photos/' + encodeURIComponent(photoId), { cache: 'no-store',
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + getToken() },
  });
  if (!res.ok) {
    const t = await res.text().catch(function () { return ''; });
    throw new Error('delete ' + res.status + ': ' + t.slice(0, 140));
  }
  return res.json();
}

// Full single-photo flow: compress → sign → PUT → record
async function uploadOne(visitId, sectionKey, file) {
  const blob = await compressImage(file);
  const signed = await signUpload(visitId, sectionKey);
  await putToStorage(signed.upload_url, blob);
  return recordMetadata(visitId, sectionKey, signed.storage_path);  // returns recorded row
}


// ──────────────────────────────────────────────────────────────────
// UI component
// ──────────────────────────────────────────────────────────────────

// renderPhotoUpload(opts)
//   opts.visitId      — store_visits.id (required; null shows "save first")
//   opts.sectionKey   — section_key for these photos (e.g. 'shopFront', 'overall')
//   opts.container    — DOM element to render into
//   opts.photos       — optional array of existing photo rows [{id, signed_url}]
function renderPhotoUpload(opts) {
  const container = opts.container;
  const visitId = opts.visitId;
  const sectionKey = opts.sectionKey;
  let photos = (opts.photos || []).slice();

  if (!container) { console.warn('[photoUpload] container required'); return; }

  if (!visitId) {
    container.innerHTML =
      '<div style="padding:12px 16px;background:var(--warn-bg);border:1px solid var(--learn-warn-line);border-radius:8px;color:var(--warn-deep);font-size:14px;">' +
      'Save the visit first, then you can add photos.</div>';
    return;
  }

  function thumb(photo) {
    return (
      '<div style="position:relative;width:80px;height:80px;border-radius:8px;overflow:hidden;border:1px solid var(--border);">' +
        '<img src="' + escapeHtml(photo.signed_url || '') + '" alt="" ' +
             'style="width:100%;height:100%;object-fit:cover;display:block;">' +
        '<button type="button" class="svr-photo-del" data-id="' + escapeHtml(photo.id) + '" ' +
                'title="Remove" ' +
                'style="position:absolute;top:2px;right:2px;width:20px;height:20px;border-radius:50%;' +
                'border:none;background:rgba(0,0,0,0.6);color:#fff;cursor:pointer;font-size:12px;line-height:1;">' +
          '×' +
        '</button>' +
      '</div>'
    );
  }

  function render() {
    const atCap = photos.length >= MAX_PHOTOS_PER_SECTION;
    container.innerHTML =
      '<div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;">' +
        photos.map(thumb).join('') +
        (atCap
          ? '<span style="font-size:12px;color:var(--tiny);">Max ' + MAX_PHOTOS_PER_SECTION + ' photos</span>'
          : '<label style="width:80px;height:80px;border:2px dashed var(--border-strong);border-radius:8px;' +
              'display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--sub);font-size:28px;">' +
              '+' +
              '<input type="file" accept="image/*" capture="environment" class="svr-photo-input" ' +
                     'style="display:none;">' +
            '</label>') +
      '</div>' +
      '<div class="svr-photo-status" style="margin-top:6px;font-size:12px;color:var(--sub);"></div>';

    const statusEl = container.querySelector('.svr-photo-status');

    const input = container.querySelector('.svr-photo-input');
    if (input) {
      input.addEventListener('change', async function () {
        const file = input.files && input.files[0];
        if (!file) return;
        statusEl.textContent = 'Uploading…';
        try {
          const recorded = await uploadOne(visitId, sectionKey, file);
          photos.push(recorded);
          if (typeof opts.onChange === 'function') opts.onChange(photos);
          render();
        } catch (e) {
          statusEl.textContent = '⚠️ ' + e.message;
          statusEl.style.color = 'var(--ui-bad)';
        }
      });
    }

    container.querySelectorAll('.svr-photo-del').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        const id = btn.dataset.id;
        if (!window.confirm('Remove this photo?')) return;
        statusEl.textContent = 'Removing…';
        try {
          await deletePhoto(id);
          photos = photos.filter(function (p) { return String(p.id) !== String(id); });
          if (typeof opts.onChange === 'function') opts.onChange(photos);
          render();
        } catch (e) {
          statusEl.textContent = '⚠️ ' + e.message;
          statusEl.style.color = 'var(--ui-bad)';
        }
      });
    });
  }

  render();
}

// Fetch existing photos for a visit (used when reopening a draft)
async function fetchPhotos(visitId) {
  const res = await fetch(getWorkerUrl() + '/api/store-visit-photos?visit_id=' + encodeURIComponent(visitId), { cache: 'no-store',
    headers: { 'Authorization': 'Bearer ' + getToken() },
  });
  if (!res.ok) throw new Error('fetch photos ' + res.status);
  return res.json();
}

// Window re-exposure (per ELC Shift module convention)
window.renderPhotoUpload = renderPhotoUpload;
window.fetchVisitPhotos = fetchPhotos;

export {
  renderPhotoUpload,
  fetchPhotos,
  uploadOne,
  compressImage,
  deletePhoto,
};
